import { useEffect, useState } from "react";

/**
 * Theme system for the Broadside web app.
 *
 * A *preference* is what the user chose: "system", "light", or "dark", persisted
 * in localStorage. The *effective* theme is what actually paints: always "light"
 * or "dark". When the preference is "system" we resolve it against the OS via
 * `matchMedia` and re-resolve whenever the OS flips.
 *
 * `document.documentElement.dataset.theme` is ALWAYS the resolved value
 * ("light" | "dark"), never "system" — CSS keys off it directly. The inline
 * script in index.html applies the same logic before first paint to avoid a
 * flash of the wrong theme; this module keeps it in sync at runtime.
 */

export type ThemePreference = "system" | "light" | "dark";
export type EffectiveTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "broadside-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Narrow an arbitrary value to a valid preference, defaulting to "system". */
function coercePreference(value: unknown): ThemePreference {
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

/** Read the stored preference; missing or garbage values resolve to "system". */
export function getStoredPreference(): ThemePreference {
  try {
    return coercePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    // localStorage can throw (private mode, disabled storage) — fail to system.
    return "system";
  }
}

/** Resolve a preference to the concrete theme that should paint right now. */
export function resolveEffective(pref: ThemePreference): EffectiveTheme {
  if (pref === "light" || pref === "dark") return pref;
  try {
    return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
  } catch {
    return "dark";
  }
}

/** Write the resolved theme onto the document root so CSS can react to it. */
export function applyTheme(pref: ThemePreference = getStoredPreference()): EffectiveTheme {
  const effective = resolveEffective(pref);
  try {
    document.documentElement.dataset.theme = effective;
  } catch {
    // No document (non-browser context) — nothing to paint.
  }
  return effective;
}

// --- System-change subscription -------------------------------------------
//
// While the preference is "system" we listen for OS theme changes and re-apply.
// The listener is torn down the moment the preference stops being "system".

let systemMedia: MediaQueryList | null = null;
let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

function stopSystemSubscription(): void {
  if (systemMedia && systemListener) {
    systemMedia.removeEventListener("change", systemListener);
  }
  systemMedia = null;
  systemListener = null;
}

function startSystemSubscription(): void {
  stopSystemSubscription();
  try {
    systemMedia = window.matchMedia(DARK_QUERY);
    systemListener = () => {
      applyTheme("system");
      emit(getStoredPreference());
    };
    systemMedia.addEventListener("change", systemListener);
  } catch {
    // matchMedia unavailable — OS following simply won't update live.
  }
}

/** Bring the system subscription in line with the given preference. */
function syncSystemSubscription(pref: ThemePreference): void {
  if (pref === "system") startSystemSubscription();
  else stopSystemSubscription();
}

// --- Preference store (persist + apply + notify) --------------------------

const subscribers = new Set<(pref: ThemePreference) => void>();

function emit(pref: ThemePreference): void {
  for (const fn of subscribers) fn(pref);
}

/**
 * Persist a new preference, repaint, manage the OS subscription, and notify any
 * mounted hooks so every control stays in sync.
 */
export function setPreference(pref: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Persisting failed — still apply for this session.
  }
  applyTheme(pref);
  syncSystemSubscription(pref);
  emit(pref);
}

/**
 * Boot the runtime theme layer once at app startup: re-apply the stored
 * preference (the inline script already did this pre-paint, so this is a
 * no-op repaint) and establish the OS subscription so a "system" preference
 * follows live theme switches for the whole session — not only while the
 * Appearance control is mounted.
 */
export function initTheme(): void {
  const pref = getStoredPreference();
  applyTheme(pref);
  syncSystemSubscription(pref);
}

// --- React hook ------------------------------------------------------------

/**
 * `[pref, setPref]` bound to the shared preference store. Multiple instances
 * (Control Center, an optional topbar toggle) stay in lockstep, and the OS
 * subscription is (re)established on mount so "system" follows live changes.
 */
export function useThemePreference(): [ThemePreference, (pref: ThemePreference) => void] {
  const [pref, setPref] = useState<ThemePreference>(getStoredPreference);

  useEffect(() => {
    // Ensure the OS subscription matches the current preference even when the
    // app booted from a stored "system" without any user interaction.
    syncSystemSubscription(getStoredPreference());

    const onChange = (next: ThemePreference) => setPref(next);
    subscribers.add(onChange);
    return () => {
      subscribers.delete(onChange);
    };
  }, []);

  return [pref, setPreference];
}
